import styled, { keyframes } from 'styled-components';
import Image from 'next/image';
import { colors } from '../utils/theme';
import media from 'styled-media-query';
import React from 'react';
import Link from 'next/link';
import getAmplitude from '../utils/amplitude';

const appear = keyframes`
    0% {
       opacity: 0;
    }

    100% {
     opacity: 1;
    }
`;
// Small
const SmallWrapper = styled.div<{ total: number }>`
  overflow-x: auto;
  padding: 8px 16px;

  display: grid;
  grid-gap: 16px;
  grid-template-columns: repeat(${p => p.total + 1}, 1fr);

  &:last-child:after {
    content: ' ';
    width: 0.1px;
  }

  ${media.greaterThan('large')`
    grid-template-columns: repeat(3, 1fr) !important;
    `};

  ${media.greaterThan('medium')`
    grid-template-columns: repeat(2, 1fr);
    `}
  animation: ${appear} 0.5s ease-in-out;
`;

const SmallCardWrapper = styled.div<{ index: number }>`
  min-width: 192px;
  background-color: ${p => (p.index % 2 === 0 ? colors.green : colors.lightGreen)};
  filter: drop-shadow(0px 4px 2px rgba(0, 0, 0, 0.25));
  border-radius: 4px;
  white-space: pre-wrap;
  ${media.greaterThan('medium')`
    min-width: 156px;
  `}
`;

const SmallImage = styled(Image)`
  border-radius: 4px 4px 0 0;
`;

const SmallTitle = styled.h4`
  height: 32px;
  margin: 8px;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  line-height: 16px;
  color: ${colors.white};
`;

const SmallDescription = styled.div`
  margin: 0 8px 8px;
  -webkit-line-clamp: 4;
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  line-height: 16px;
  font-size: 12px;
  color: ${colors.white};
`;

const SmallReadMore = styled.div`
  padding: 4px 8px;
  font-size: 12px;
  font-weight: bold;
  color: ${colors.green};
  display: flex;
  align-items: center;
  color: ${colors.white};
`;

const SmallIndex = styled.div<{ index: number }>`
  background-color: ${p => (p.index % 2 === 0 ? colors.green : colors.lightGreen)};
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: ${colors.white};
  font-weight: 600;
  display: flex;
  position: absolute;
  left: 4px;
  top: 4px;
  height: 28px;
  width: 28px;
  z-index: 1;
`;

// End small

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
  ${media.lessThan('small')`
    flex-direction: column;
  `}
  animation: ${appear} 0.5s ease-in-out;
  cursor: pointer;
`;
const AttractionWrapper = styled.div<{ index: number }>`
  width: 85%;
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 8px;
  background-color: ${p => (p.index % 2 === 0 ? colors.green : colors.lightGreen)};
  padding: 12px 0px 12px 16px;
  ${media.lessThan('small')`
    flex-direction: column;
    padding: 0px;
    margin-top: 16px;
  `}
`;

const Attraction = styled(Image)`
  display: flex;
  border-radius: 8px;
  width: 260px;
  height: 200px;
  ${media.lessThan('small')`
    margin-top: 16px;
  `}
`;

const Index = styled.div<{ index: number }>`
  background-color: ${p => (p.index % 2 === 0 ? colors.green : colors.lightGreen)};
  border-radius: 50%;
  height: 64px;
  width: 64px;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: ${colors.white};
  font-weight: 600;
  display: flex;
`;

const AttractionText = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: justify;
  color: ${colors.white};
  justify-content: space-between;
  height: 130px;
  padding: 0 12px;
  ${media.lessThan('small')`
    padding: 16px;
  `}
`;

const AttractionTitle = styled.h6`
  font-size: 18px;
  margin: 0;
  ${media.lessThan('small')`
      font-size: 14px;
    `}
`;
const AttractionDescription = styled.p`
  font-size: 14px;
  margin: 0;
  ${media.lessThan('small')`
      font-size: 12px;
    `}
`;
const ReadMore = styled.h2`
  font-size: 14px;
  margin: 0;
`;

const SmallCard = (id, image, title, description, index) => {
  return (
    <SmallCardWrapper key={id} index={index}>
      <SmallIndex index={index}>
        {' '}
        <div>{index + 1}.</div>{' '}
      </SmallIndex>
      <SmallImage alt={title.trim()} src={image} width="300" height="200" />
      <SmallTitle>{title.trim()}</SmallTitle>
      <SmallDescription>{description?.trim()}</SmallDescription>
      <SmallReadMore>READ MORE &gt;</SmallReadMore>
    </SmallCardWrapper>
  );
};

const smallAttractions = attractions => {
  if (!attractions.Attraction) return null;
  return (
    <SmallWrapper total={attractions.Attraction.length || 0}>
      {attractions.Attraction.map((att, index) => SmallCard(att.id, att.image, att.title, att.description, index))}
    </SmallWrapper>
  );
};

const normalAttractions = attractions => {
  if (!attractions.Attraction) return null;
  return attractions.Attraction.map((att, index) => (
    <Link href={`/${att.app.name.toLowerCase()}/attraction/${att.slug}`} key={att.id} passHref>
      <Wrapper
        onClick={async () => {
          const amplitude = await getAmplitude();
          amplitude.logEvent(`Clicked on tour attraction`);
        }}>
        <Index index={index}>
          {' '}
          <div>{index + 1}.</div>{' '}
        </Index>
        <AttractionWrapper index={index}>
          <Attraction key={att.id} src={att.image} width={'260'} height={'200'} alt={att.title} />
          <AttractionText>
            <AttractionTitle>{att.title.trim()}</AttractionTitle>
            <AttractionDescription>
              {att.description.length > 80 ? att.description.trim().slice(0, 76) + '...' : att.description.trim()}
            </AttractionDescription>
            <ReadMore>READ MORE &gt;</ReadMore>
          </AttractionText>
        </AttractionWrapper>
      </Wrapper>
    </Link>
  ));
};

const Attractions = ({ attractions, size }) => {
  if (!attractions || attractions.length === 0) return null;
  return size.width > 450 ? normalAttractions(attractions) : smallAttractions(attractions);
};

export default Attractions;
